import { Component, forwardRef, inject, OnInit, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TabComponent } from '../../../components/tab/tab.component';
import { TabLayoutComponent } from '../../../components/tab-layout/tab-layout.component';
import { APITableComponent } from '../../../components/table/api-table/api-table.component';
import { ButtonDirective } from '../../../components/button/button.directive';
import { NgIcon } from '@ng-icons/core';
import { TableRow } from '../../../components/table/table.interface';
import { environment } from '../../../../environments/environment';
import { lastValueFrom } from 'rxjs';
import { SnackbarService } from '../../../services/snackbar.service';
import { AuthenticationService } from '../../../services/authentication.service';
import { isManagementRole } from '../../../utils/user-roles';

@Component({
  selector: 'app-id-browser-tab',
  imports: [TabLayoutComponent, APITableComponent, ButtonDirective, NgIcon],
  providers: [
    {
      provide: TabComponent,
      useExisting: forwardRef(() => IdBrowserTabComponent),
    },
  ],
  templateUrl: './id-browser-tab.component.html',
  styleUrl: './id-browser-tab.component.scss',
})
export class IdBrowserTabComponent extends TabComponent implements OnInit {
  override name: string = this.dictionaryService.get('IdBrowser');

  private http = inject(HttpClient);
  private snackbarService = inject(SnackbarService);
  private authService = inject(AuthenticationService);
  private baseUrl = environment.apiBaseUrl;
  private readonly assignedCustomerIds = signal<ReadonlySet<string>>(new Set());

  /** Whether the current user belongs to the management role group. */
  isManagement = false;

  async ngOnInit(): Promise<void> {
    const userData = await this.authService.getUserData();
    this.isManagement = isManagementRole(userData?.role);
  }

  public async main(): Promise<void> { }

  isCustomerAssigned(row: TableRow): boolean {
    const customerId = row.value['id'];

    return (
      row.value['Is Assigned'] === true ||
      (typeof customerId === 'string' &&
        this.assignedCustomerIds().has(customerId))
    );
  }

  /**
   * Handles adding a single customer
   */
  handleRowSelect = async (row: TableRow): Promise<void> => {
    const customerId = row.value['id'] as string;
    if (!customerId) {
      this.snackbarService.showError('Invalid customer ID');
      return;
    }
    await this.assignCustomersToUser([customerId]);
  };

  /**
   * Handles bulk adding customers
   */
  handleBulkSelect = async (rows: TableRow[]): Promise<void> => {
    const customerIds = rows
      .map(row => row.value['id'] as string)
      .filter(id => !!id);

    if (customerIds.length === 0) {
      this.snackbarService.showWarning('NoCustomersSelected');
      return;
    }

    await this.assignCustomersToUser(customerIds);
  };

  /**
   * Assigns customers to the current user via PATCH API
   * Uses the /api-v3/users/customers endpoint
   */
  private async assignCustomersToUser(customerIds: string[]): Promise<void> {
    try {
      const endpoint = `${this.baseUrl}/users/customers`;

      const body = { customerIds };

      console.log('Assigning customers to user:', body);

      const response = await lastValueFrom(
        this.http.patch<{ success: boolean; message: string }>(endpoint, body)
      );

      if (response.success) {
        this.assignedCustomerIds.update((assignedIds) => {
          const updatedIds = new Set(assignedIds);
          customerIds.forEach((customerId) => updatedIds.add(customerId));
          return updatedIds;
        });

        const messageKey = customerIds.length === 1
          ? 'CustomerAddedSuccess'
          : 'CustomersAddedSuccess';
        this.snackbarService.showSuccess(messageKey);
        console.log('Customers assigned successfully:', response);
      } else {
        this.snackbarService.showError('CustomerAddError');
        console.error('Failed to assign customers:', response.message);
      }
    } catch (error) {
      this.snackbarService.showError('CustomerAddError');
      console.error('Failed to assign customers:', error);
    }
  }
}
